import { useCallback, useEffect, useState } from "react";
import { Copy, KeyRound, ShieldOff } from "lucide-react";
import { agenteSisodontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useSessao } from "../../context/SessaoContext";
import { Button, Modal } from "../../components/ui";

const INTERVALO_STATUS_MS = 15000;

const formatarContato = (instante) => {
  if (!instante) {
    return "Nunca conectou";
  }
  return new Date(instante).toLocaleString("pt-BR");
};

/**
 * Seção "Robô local": estado do agente no PC do usuário e gerenciamento da
 * credencial. O token só existe na resposta de "Gerar credencial" e é
 * descartado do estado assim que o diálogo é fechado.
 */
const RoboLocalSisodonto = () => {
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();
  const { organizacaoAtiva } = useSessao();

  const [status, setStatus] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [tokenGerado, setTokenGerado] = useState(null);

  const carregarStatus = useCallback(async () => {
    try {
      setStatus(await agenteSisodontoService.status());
    } catch (erro) {
      setStatus((anterior) => anterior || { online: false });
    }
  }, []);

  useEffect(() => {
    carregarStatus();
    const intervalo = setInterval(carregarStatus, INTERVALO_STATUS_MS);
    return () => clearInterval(intervalo);
  }, [carregarStatus]);

  const gerarCredencial = async () => {
    setProcessando(true);
    try {
      const credencial = await agenteSisodontoService.gerarCredencial();
      setTokenGerado(credencial.token);
      await carregarStatus();
    } catch (erro) {
      showError(erro.message || "Erro ao gerar a credencial do robô");
    } finally {
      setProcessando(false);
    }
  };

  const revogarCredencial = async () => {
    const confirmado = await confirmDialog({
      title: "Revogar credencial do robô",
      description:
        "O robô deixa de conseguir se conectar e a integração com o Sisodonto para até você gerar e salvar uma nova credencial. Deseja revogar?",
      confirmText: "Revogar",
      variant: "danger",
    });
    if (!confirmado) {
      return;
    }

    setProcessando(true);
    try {
      await agenteSisodontoService.revogarCredencial();
      showSuccess("Credencial revogada.");
      await carregarStatus();
    } catch (erro) {
      showError(erro.message || "Erro ao revogar a credencial do robô");
    } finally {
      setProcessando(false);
    }
  };

  const copiarToken = async () => {
    try {
      await navigator.clipboard.writeText(tokenGerado);
      showSuccess("Token copiado.");
    } catch (erro) {
      showError(
        "Não foi possível copiar. Selecione o texto e copie manualmente.",
      );
    }
  };

  const fecharToken = () => setTokenGerado(null);

  const online = Boolean(status?.online);
  const credencialAtiva = Boolean(status?.credencialAtiva);

  return (
    <section
      className="mt-6 rounded-lg border bg-white p-6 shadow"
      aria-labelledby="robo-local-titulo"
    >
      <h2
        id="robo-local-titulo"
        className="mb-1 text-lg font-semibold text-gray-900"
      >
        Robô local
      </h2>
      <p className="mb-4 text-sm text-gray-600">
        O robô roda no PC do usuário e busca os valores no Sisodonto quando um
        administrador pede uma data em Caixa Geral. Ele só faz conexões de
        saída; você liga abrindo o programa e desliga fechando a janela.
      </p>

      <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div>
          <dt className="text-gray-500">Estado</dt>
          <dd
            data-testid="estado-robo-config"
            className={
              online
                ? "font-medium text-green-700"
                : "font-medium text-gray-600"
            }
          >
            {online ? "Robô online" : "Robô offline"}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Último contato</dt>
          <dd>{formatarContato(status?.ultimoContatoEm)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Versão do robô</dt>
          <dd>{status?.versaoAgente || "—"}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Credencial</dt>
          <dd>
            {credencialAtiva
              ? `Ativa (${status.credencialPrefixo}…)`
              : "Nenhuma credencial ativa"}
          </dd>
        </div>
        <div className="md:col-span-2">
          <dt className="text-gray-500">
            Identificador da organização (ORGANIZACAO_ID do robô)
          </dt>
          <dd className="break-all font-mono text-xs">
            {organizacaoAtiva?.id || "—"}
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          icon={KeyRound}
          onClick={gerarCredencial}
          loading={processando}
          disabled={credencialAtiva}
          title={
            credencialAtiva
              ? "Revogue a credencial atual antes de gerar outra"
              : ""
          }
        >
          Gerar credencial
        </Button>
        <Button
          variant="danger"
          icon={ShieldOff}
          onClick={revogarCredencial}
          disabled={!credencialAtiva || processando}
        >
          Revogar
        </Button>
      </div>

      <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="mb-2 font-medium">Como instalar no PC do robô</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            Instale o robô e preencha o endereço do PCOE e o identificador
            acima.
          </li>
          <li>
            Gere a credencial aqui e salve o token no robô (“Salvar-Token”).
          </li>
          <li>Faça o login no Sisodonto pelo robô (“Login-Sisodonto”).</li>
          <li>
            Abra “Ligar-Robo” e deixe a janela aberta. O estado acima vira
            “online”.
          </li>
        </ol>
      </div>

      <Modal
        open={tokenGerado !== null}
        onClose={fecharToken}
        title="Credencial do robô gerada"
        className="max-w-lg"
      >
        <p className="mb-3 text-sm text-gray-700">
          Guarde este token agora: <strong>ele não será exibido de novo</strong>
          . Cole-o no robô quando o “Salvar-Token” pedir. Não o envie por e-mail
          ou mensagem.
        </p>
        <input
          readOnly
          aria-label="Token do robô"
          value={tokenGerado || ""}
          onFocus={(evento) => evento.target.select()}
          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" icon={Copy} onClick={copiarToken}>
            Copiar
          </Button>
          <Button onClick={fecharToken}>Já guardei o token</Button>
        </div>
      </Modal>
    </section>
  );
};

export default RoboLocalSisodonto;
